import React, { useState } from 'react';
import {
    Box, Typography, Grid, Card, CardContent, CardActionArea,
    Chip, Button, Divider, Fade, Avatar
} from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

/**
 * BranchSelectorPage
 *
 * Shown ONLY to Company Admin after login (when multi-branch is enabled).
 * Allows them to:
 *   1. Pick a specific branch  → work in that branch's context
 *   2. Pick "Head Office View" → see aggregate data across all branches
 *
 * Branch Admin / Branch User never see this screen.
 */
const BranchSelectorPage = () => {
    const navigate  = useNavigate();
    const { tenant, allBranches, switchBranch, user } = useAuth();

    const [hovered, setHovered] = useState(null);

    const handleSelectBranch = (branch) => {
        switchBranch(branch); // null = All Branches (Head Office aggregate)
        navigate('/dashboard');
    };

    // Sort: Head Office first, then alphabetically
    const sortedBranches = [...allBranches].sort((a, b) => {
        if (a.is_main) return -1;
        if (b.is_main) return 1;
        return a.name.localeCompare(b.name);
    });

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0C3D12 0%, #1B5E20 50%, #2E7D32 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 2, md: 6 },
        }}>
            <Fade in timeout={500}>
                <Box sx={{ width: '100%', maxWidth: 900 }}>

                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 5 }}>
                        <Typography variant="h3" sx={{
                            fontWeight: 900,
                            color: '#fff',
                            letterSpacing: 1,
                            mb: 1,
                            fontFamily: "'Outfit', sans-serif",
                        }}>
                            {tenant?.company_name || 'CSM Pro'}
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 300 }}>
                            नमस्ते, {user?.name}! कृपया तुमचा Branch निवडा
                        </Typography>
                    </Box>

                    {/* "Head Office — All Branches" Card */}
                    <Card
                        onMouseEnter={() => setHovered('all')}
                        onMouseLeave={() => setHovered(null)}
                        sx={{
                            mb: 3,
                            borderRadius: 4,
                            border: hovered === 'all' ? '2px solid #FFD700' : '2px solid transparent',
                            transition: 'all 0.25s ease',
                            transform: hovered === 'all' ? 'translateY(-4px)' : 'none',
                            boxShadow: hovered === 'all'
                                ? '0 20px 40px rgba(0,0,0,0.3)'
                                : '0 8px 20px rgba(0,0,0,0.15)',
                            background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
                            cursor: 'pointer',
                        }}
                    >
                        <CardActionArea onClick={() => handleSelectBranch(null)} sx={{ p: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                    <Avatar sx={{
                                        width: 60, height: 60,
                                        bgcolor: 'rgba(255,215,0,0.2)',
                                        border: '2px solid #FFD700',
                                    }}>
                                        <AccountBalanceIcon sx={{ color: '#FFD700', fontSize: 32 }} />
                                    </Avatar>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                                            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800 }}>
                                                Head Office View
                                            </Typography>
                                            <Chip
                                                label="सर्व Branches"
                                                size="small"
                                                sx={{ bgcolor: '#FFD700', color: '#1a237e', fontWeight: 'bold' }}
                                            />
                                        </Box>
                                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                            सर्व branches चा एकत्रित (Aggregate) डेटा बघा
                                        </Typography>
                                    </Box>
                                    {hovered === 'all' && (
                                        <CheckCircleIcon sx={{ color: '#FFD700', fontSize: 32 }} />
                                    )}
                                </Box>
                            </CardContent>
                        </CardActionArea>
                    </Card>

                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 3 }}>
                        <Chip label="किंवा एखाद्या Branch मध्ये काम करा" sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.1)', fontSize: '0.8rem' }} />
                    </Divider>

                    {/* Individual Branch Cards */}
                    <Grid container spacing={3}>
                        {sortedBranches.map((branch) => (
                            <Grid item xs={12} sm={6} md={4} key={branch.id}>
                                <Card
                                    onMouseEnter={() => setHovered(branch.id)}
                                    onMouseLeave={() => setHovered(null)}
                                    sx={{
                                        borderRadius: 4,
                                        height: '100%',
                                        border: hovered === branch.id
                                            ? '2px solid #0FA958'
                                            : '2px solid rgba(255,255,255,0.15)',
                                        transition: 'all 0.25s ease',
                                        transform: hovered === branch.id ? 'translateY(-6px)' : 'none',
                                        boxShadow: hovered === branch.id
                                            ? '0 20px 40px rgba(0,0,0,0.4)'
                                            : '0 4px 16px rgba(0,0,0,0.2)',
                                        background: 'rgba(255,255,255,0.07)',
                                        backdropFilter: 'blur(12px)',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <CardActionArea
                                        onClick={() => handleSelectBranch(branch)}
                                        sx={{ height: '100%', p: 1 }}
                                    >
                                        <CardContent>
                                            {/* Branch Icon + Main Badge */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Avatar sx={{
                                                    width: 50, height: 50,
                                                    bgcolor: branch.is_main
                                                        ? 'rgba(255,215,0,0.2)'
                                                        : 'rgba(15,169,88,0.2)',
                                                    border: `2px solid ${branch.is_main ? '#FFD700' : '#0FA958'}`,
                                                }}>
                                                    <StoreIcon sx={{
                                                        color: branch.is_main ? '#FFD700' : '#0FA958',
                                                        fontSize: 26,
                                                    }} />
                                                </Avatar>
                                                {branch.is_main && (
                                                    <Chip
                                                        label="Main"
                                                        size="small"
                                                        sx={{ bgcolor: 'rgba(255,215,0,0.15)', color: '#FFD700', fontWeight: 'bold', fontSize: '0.7rem' }}
                                                    />
                                                )}
                                            </Box>

                                            {/* Branch Name */}
                                            <Typography variant="h6" sx={{
                                                color: '#fff',
                                                fontWeight: 800,
                                                mb: 1,
                                                fontFamily: "'Outfit', sans-serif",
                                            }}>
                                                {branch.name}
                                            </Typography>

                                            {/* Branch Address */}
                                            {branch.address && (
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                                    <LocationOnIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 16, mt: 0.3 }} />
                                                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem' }}>
                                                        {branch.address}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Hover action hint */}
                                            {hovered === branch.id && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Chip
                                                        icon={<CheckCircleIcon sx={{ fontSize: '16px !important' }} />}
                                                        label="येथे काम करा"
                                                        size="small"
                                                        sx={{ bgcolor: '#0FA958', color: '#fff', fontWeight: 'bold' }}
                                                    />
                                                </Box>
                                            )}
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Footer */}
                    <Box sx={{ textAlign: 'center', mt: 5 }}>
                        <Button
                            variant="text"
                            size="small"
                            sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}
                            onClick={() => { localStorage.clear(); window.location.href = '/login'; }}
                        >
                            वेगळ्या Account ने Login करा
                        </Button>
                    </Box>

                </Box>
            </Fade>
        </Box>
    );
};

export default BranchSelectorPage;
